import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { Table, Typography } from 'antd';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { ZD_ATTACHMENTS } from 'queries/attachments';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import { formatFileName } from 'utilities/helpers';
import { formatDateTimeWithWeekDay } from 'utilities/datesAndTimes';

const { Text } = Typography;

const ZendeskAttachments = () => {
  const { customerInfo } = useCustomerInfo();
  const { applicationId, customerId } = useParams();

  // REVIEW: No error handling
  const [getAttachments, attachmentsQuery] = useLazyQuery(ZD_ATTACHMENTS);

  const attachments = useMemo(() => {
    const legacyAttachments = attachmentsQuery.data?.attachments || [];
    const zdAttachments = (
      attachmentsQuery.data?.zd_attachments?.content || []
    ).map((a) => {
      return {
        url: a.contentUrl,
        date: a.insertTz,
        name: a.name,
      };
    });

    return [...zdAttachments, ...legacyAttachments].sort(
      (a, b) => -a.date?.localeCompare(b.date),
    );
  }, [attachmentsQuery.data]);

  useEffect(() => {
    if (customerInfo?.email) {
      getAttachments({
        variables: { email: customerInfo?.email, customerId, applicationId },
      });
    }
  }, [customerInfo?.email, customerId, applicationId]);

  const columns = [
    {
      title: 'File',
      dataIndex: 'file',
      key: 'file',
      render: (_, record) => (
        <Text>
          <a
            data-testid={`${record.url}-${record.name}`}
            onClick={() =>
              window.open(record.url, '_blank', 'noopener,noreferrer')
            }
          >
            {formatFileName(record.name)}
          </a>
        </Text>
      ),
    },
    {
      title: 'Last Modified',
      dataIndex: 'date',
      key: 'date',
      render: (date) => <Text>{date && formatDateTimeWithWeekDay(date)}</Text>,
    },
  ];

  return (
    <QueryBoundary
      query={attachmentsQuery}
      loadingMessage="Loading zendesk attachments"
    >
      {attachments?.length > 0 ? (
        <Table
          size="small"
          dataSource={attachments}
          columns={columns}
          pagination={false}
          rowKey={(record) => `key-${record.date}-${record.name}`}
          style={{ width: '100%' }}
        />
      ) : (
        'No attachments found...'
      )}
    </QueryBoundary>
  );
};

export default ZendeskAttachments;
